import { LANGUAGES } from '@intone/shared/constants';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';

type FieldDescriptionProps = {
  children: string;
};

function FieldDescription({ children }: FieldDescriptionProps) {
  return <p className="text-detail text-gray-500 leading-tight">{children}</p>;
}

type ModelFieldProps = {
  value: string;
  onUpdate: (value: string) => void;
  modelList: {
    value: string;
    label: string;
  }[];
};

export function ModelField({ value, onUpdate, modelList }: ModelFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-model" className="font-medium text-gray-900">
        Model<sup className="text-destructive">*</sup>
      </Label>
      <FieldDescription>Speech recognition model to use</FieldDescription>
      <Select value={value} onValueChange={onUpdate}>
        <SelectTrigger id="stt-model" className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {modelList.map((model) => (
            <SelectItem key={model.value} value={model.value}>
              {model.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type LanguageFieldProps = {
  value: string;
  onUpdate: (value: string) => void;
};

export function LanguageField({ value, onUpdate }: LanguageFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-language" className="font-medium text-gray-900">
        Language<sup className="text-destructive">*</sup>
      </Label>
      <FieldDescription>Primary language for speech recognition</FieldDescription>
      <Select value={value} onValueChange={onUpdate}>
        <SelectTrigger id="stt-language" className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LANGUAGES.map((lang) => (
            <SelectItem key={lang.value} value={lang.value}>
              {lang.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

type BooleanFieldProps = {
  value: boolean;
  onUpdate: (value: boolean) => void;
};

export function UseFillerWordsField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-filler"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-filler" className="font-medium text-gray-900 cursor-pointer">
          Use Filler Words
        </Label>
      </div>
      <FieldDescription>Include filler words (um, uh) in the transcript</FieldDescription>
    </div>
  );
}

export function DetectLanguageField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-detect-lang"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-detect-lang" className="font-medium text-gray-900 cursor-pointer">
          Detect Language
        </Label>
      </div>
      <FieldDescription>Automatic detection of speech language</FieldDescription>
    </div>
  );
}

export function InterimResultsField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-interim"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-interim" className="font-medium text-gray-900 cursor-pointer">
          Interim Results
        </Label>
      </div>
      <FieldDescription>Return intermediate transcription results in real time</FieldDescription>
    </div>
  );
}

export function PunctuateField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-punctuate"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-punctuate" className="font-medium text-gray-900 cursor-pointer">
          Punctuate
        </Label>
      </div>
      <FieldDescription>Automatically add punctuation</FieldDescription>
    </div>
  );
}

export function NumeralsField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-numerals"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-numerals" className="font-medium text-gray-900 cursor-pointer">
          Numerals
        </Label>
      </div>
      <FieldDescription>Convert spoken numbers to digits ("three" to "3")</FieldDescription>
    </div>
  );
}

export function SmartFormatField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-smart-format"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-smart-format" className="font-medium text-gray-900 cursor-pointer">
          Smart Format
        </Label>
      </div>
      <FieldDescription>Smart formatting for dates, currencies, phone numbers</FieldDescription>
    </div>
  );
}

export function SpokenPunctuationField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-spoken-punctuation"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-spoken-punctuation" className="font-medium text-gray-900 cursor-pointer">
          Spoken Punctuation
        </Label>
      </div>
      <FieldDescription>Recognize spoken punctuation literally ("period" to ".")</FieldDescription>
    </div>
  );
}

export function ProfanityFilterField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-profanity"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-profanity" className="font-medium text-gray-900 cursor-pointer">
          Profanity Filter
        </Label>
      </div>
      <FieldDescription>Mask profanity in the transcript</FieldDescription>
    </div>
  );
}

export function EnableDiarizationField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-diarization"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-diarization" className="font-medium text-gray-900 cursor-pointer">
          Enable Diarization
        </Label>
      </div>
      <FieldDescription>Distinguish between different speakers</FieldDescription>
    </div>
  );
}

export function ExplicitPunctuationField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-explicit-punctuation"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-explicit-punctuation" className="font-medium text-gray-900 cursor-pointer">
          Explicit Punctuation
        </Label>
      </div>
      <FieldDescription>Apply explicit punctuation rules</FieldDescription>
    </div>
  );
}

export function EnableWordTimeOffsetsField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-word-time-offsets"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-word-time-offsets" className="font-medium text-gray-900 cursor-pointer">
          Enable Word Time Offsets
        </Label>
      </div>
      <FieldDescription>Include timestamps for each word</FieldDescription>
    </div>
  );
}

export function EnableWordConfidenceField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-word-confidence"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-word-confidence" className="font-medium text-gray-900 cursor-pointer">
          Enable Word Confidence
        </Label>
      </div>
      <FieldDescription>Include confidence score for each word</FieldDescription>
    </div>
  );
}

export function EnableVoiceActivityEventsField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-voice-activity-events"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-voice-activity-events" className="font-medium text-gray-900 cursor-pointer">
          Enable Voice Activity Events
        </Label>
      </div>
      <FieldDescription>Enable Voice Activity Detection (VAD) events</FieldDescription>
    </div>
  );
}

export function EnergyFilterField({ value, onUpdate }: BooleanFieldProps) {
  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-2">
        <Checkbox
          id="stt-energy"
          checked={value}
          onCheckedChange={(ch) => onUpdate(ch === true)}
        />
        <Label htmlFor="stt-energy" className="font-medium text-gray-900 cursor-pointer">
          Energy Filter
        </Label>
      </div>
      <FieldDescription>Filter out quiet/background audio with low energy</FieldDescription>
    </div>
  );
}

type NumericFieldProps = {
  value: number;
  onUpdate: (value: number) => void;
};

export function EndpointingMsField({ value, onUpdate }: NumericFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-endpointing" className="font-medium text-gray-900">
        Endpointing (ms)
      </Label>
      <FieldDescription>
        Silence duration (ms) before a sentence is complete. Lower = faster, but may truncate speech
      </FieldDescription>
      <Input
        id="stt-endpointing"
        type="number"
        value={value}
        onChange={(e) => onUpdate(parseInt(e.target.value) || 0)}
        onWheel={(e) => e.currentTarget.blur()}
        className="h-8 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        min={0}
        max={5000}
      />
    </div>
  );
}

export function MinConfidenceThresholdField({ value, onUpdate }: NumericFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-confidence" className="font-medium text-gray-900">
        Min Confidence Threshold
      </Label>
      <FieldDescription>
        Minimum confidence (0-1) to accept transcription. Results below are discarded
      </FieldDescription>
      <Input
        id="stt-confidence"
        type="number"
        step={0.01}
        min={0}
        max={1}
        value={value}
        onChange={(e) => onUpdate(parseFloat(e.target.value) || 0)}
        onWheel={(e) => e.currentTarget.blur()}
        className="h-8 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
    </div>
  );
}

type JSONFieldProps = {
  value: string[];
  onUpdate: (value: string[]) => void;
};

export function KeytermsField({ value, onUpdate }: JSONFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-keyterms" className="font-medium text-gray-900">
        Keyterms (each on a new line)
      </Label>
      <FieldDescription>
        Key terms for improved recognition of specific terminology
      </FieldDescription>
      <Textarea
        id="stt-keyterms"
        value={Array.isArray(value) ? value.join('\n') : ''}
        onChange={(e) => {
          const lines = e.target.value.split('\n');
          onUpdate(lines);
        }}
        className="min-h-[80px]"
      />
    </div>
  );
}

export function PhraseListField({ value, onUpdate }: JSONFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-phrase-list" className="font-medium text-gray-900">
        Phrase List (each on a new line)
      </Label>
      <FieldDescription>
        Custom phrases for improved domain-specific vocabulary recognition
      </FieldDescription>
      <Textarea
        id="stt-phrase-list"
        value={Array.isArray(value) ? value.join('\n') : ''}
        onChange={(e) => {
          const lines = e.target.value.split('\n');
          onUpdate(lines);
        }}
        className="min-h-[80px]"
      />
    </div>
  );
}

type StringFieldProps = {
  value: string | null;
  onUpdate: (value: string) => void;
};

export function PromptField({ value, onUpdate }: StringFieldProps) {
  return (
    <div className="grid gap-1">
      <Label htmlFor="stt-prompt" className="font-medium text-gray-900">
        Prompt
      </Label>
      <FieldDescription>Hint/context for the STT model to guide transcription</FieldDescription>
      <Textarea
        id="stt-prompt"
        value={value || ''}
        onChange={(e) => onUpdate(e.target.value)}
        className="min-h-[60px]"
        placeholder="Optional prompt to guide transcription"
      />
    </div>
  );
}
